/**
 * StudioWebContext.tsx — Contexto de renderização web do Studio
 *
 * Transporta, para os blocos, os dados já resolvidos do documento e as ações
 * disponíveis (aceitar, recusar, baixar PDF). Blocos de conteúdo usam apenas os
 * dados; blocos da família `action` usam as ações.
 *
 * RELEASE: v1.1.0 - 2026-09-10 - Documento disponível no contexto (blocos de página
 *                                precisam ler `settings.forms`)
 * RELEASE: v1.0.0 - 2026-09-09 - Versão inicial (Studio, Fase 2)
 */

import React, { createContext, useContext } from 'react';
import type { PublicProposalData } from '@/hooks/usePublicProposal';
import type { StudioDocument } from '@/lib/studio/core';

/** Ações que a página hospedeira disponibiliza aos blocos interativos. */
export interface StudioWebActions {
  onAccept?: () => void;
  onReject?: () => void;
  onDownloadPdf?: () => void;
}

/** Valor do contexto de renderização web. */
export interface StudioWebContextValue {
  /** Dados da proposta (quando o documento é do tipo proposta). */
  proposal?: PublicProposalData;
  /** Documento em blocos, quando disponível (usado por blocos de página). */
  document?: StudioDocument | null;
  actions: StudioWebActions;
}

const Ctx = createContext<StudioWebContextValue>({ actions: {} });

/** Provedor usado pelo renderizador web. */
export function StudioWebProvider({
  value,
  children,
}: {
  value: StudioWebContextValue;
  children: React.ReactNode;
}) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Acesso ao contexto de renderização dentro de um bloco. */
export function useStudioWeb(): StudioWebContextValue {
  return useContext(Ctx);
}
